/**
 * Network Status Hook
 * 
 * Monitors network connectivity and provides status information
 * to help with timeout and retry logic.
 */

import { useState, useEffect } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

export const useNetworkStatus = () => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
  });

  useEffect(() => {
    const updateNetworkStatus = () => {
      const connection = (navigator as any).connection || 
                        (navigator as any).mozConnection || 
                        (navigator as any).webkitConnection;

      setNetworkStatus({
        isOnline: navigator.onLine,
        effectiveType: connection?.effectiveType,
        downlink: connection?.downlink,
        rtt: connection?.rtt,
        saveData: connection?.saveData,
      });
    };

    // Initial update
    updateNetworkStatus();

    // Listen for online/offline events
    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);

    // Listen for connection changes (if supported)
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    if (connection) {
      connection.addEventListener('change', updateNetworkStatus);
    }

    return () => {
      window.removeEventListener('online', updateNetworkStatus);
      window.removeEventListener('offline', updateNetworkStatus);
      
      if (connection) {
        connection.removeEventListener('change', updateNetworkStatus);
      }
    };
  }, []);

  // Helper functions
  const isSlowConnection = () => {
    return networkStatus.effectiveType === 'slow-2g' || 
           networkStatus.effectiveType === '2g' ||
           (networkStatus.rtt && networkStatus.rtt > 1000);
  };

  const getRecommendedTimeout = () => {
    if (!networkStatus.isOnline) return 30000; // 30s for offline
    if (isSlowConnection()) return 25000; // 25s for slow connections
    if (networkStatus.effectiveType === '3g') return 20000; // 20s for 3G
    return 15000; // 15s for fast connections
  };

  return {
    ...networkStatus,
    isSlowConnection: isSlowConnection(),
    recommendedTimeout: getRecommendedTimeout(),
  };
};